import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Label } from "@/atoms-react/text/Text.react";
import { companyDetails } from "@/features/company/types";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { formStyle } from "../form-style";
import { UploadCompanyDocumentVerified } from "./types";

type DocumentTypeProDropDownProps = {
  companyDetails: companyDetails;
  uploadDocumentData: UploadCompanyDocumentVerified;
  isAdmin: boolean;
  actionEnabled: boolean | undefined;
};

export const ProVerifiedDocumentType = ({
  companyDetails,
  uploadDocumentData,
  isAdmin = false,
  actionEnabled
}: DocumentTypeProDropDownProps) => {
  const { getLanguage } = usei18n();
  const translation = useTranslation(["pass", "error"]);
  const { t } = translation;

  // Fetch all document types inside identityDocumentTypeList
  const { getAllIdentityDocumentTypesV2Query } = useKYC();
  const { data: allCategories, isLoading: isLoadingAllCategories } =
    getAllIdentityDocumentTypesV2Query();
  const identityDocumentTypeList = allCategories ?? [];

  // Internal dropdown rendering state
  const identityDocumentType = companyDetails.legalEntity.IdentityDocument?.identity_document_type;
  const [documentTypeState, setDocumentTypeState] = useState<string | null>(
    identityDocumentType ? identityDocumentType.uuid.toString() : null
  );

  return (
    <div className={formStyle.containerLine}>
      <div className={formStyle.containerLineTitle}>
        <Label>{t("olkypass.mainPage.olkypass.verified.pro.document_type_title")}</Label>
        {/* TODO ask tooltip content :
        <div className={verifiedContentStyle.tooltipContainer}>
          <InformationCircleIcon
            className={verifiedContentStyle.tooltipContainerIcon}
            data-tooltip={t("olkypass.mainPage.olkypass.verified.pro.document_type_title")}
            data-position="right"
          />
        </div> */}
      </div>
      <div className={formStyle.containerLineContent}>
        {isLoadingAllCategories ? (
          <div className={formStyle.containerLineContentLoader}>
            <Loader />
          </div>
        ) : (
          <Dropdown
            value={documentTypeState}
            placeholder={t("olkypass.mainPage.olkypass.verified.pro.document_type_placeholder")}
            initialValue={undefined} // Disable initialValue != value logic
            required={true}
            dataCy="document_type_dropdown"
            disabled={
              (!!companyDetails.legalEntity.UserValidation || actionEnabled === false) && !isAdmin
            }
            className="w-full"
            search={true}
            onChange={async (item) => {
              if (item.key !== documentTypeState) {
                const found = identityDocumentTypeList.find(
                  (documentType) => documentType.uuid.toString() === item.key
                );
                if (!found) return;
                setDocumentTypeState(item.key);

                try {
                  await uploadDocumentData({
                    legalEntityUuid: companyDetails.legalEntity.UUID,
                    documentTypeUuid: found.uuid
                  });
                  toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
                } catch (error: unknown) {
                  setDocumentTypeState(
                    identityDocumentType ? identityDocumentType.uuid.toString() : null
                  );
                  toast.error(extractErrorDescription(error, translation));
                }
              }
            }}
            items={identityDocumentTypeList.map((documentType) => {
              return {
                key: documentType.uuid.toString(),
                value: getLanguage() === "fr" ? documentType.typeFra : documentType.typeEng
              };
            })}
          />
        )}
      </div>
    </div>
  );
};
